import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { HeatmapMonth } from "../../../types/employee-details";

interface PScoreHeatmapProps {
  pScore: number;
  heatmapData: HeatmapMonth[];
}

const PScoreHeatmap: React.FC<PScoreHeatmapProps> = ({
  pScore,
  heatmapData,
}) => {
  const getDayName = (index: number): string => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    return days[index];
  };

  const getHeatmapColor = (value: number): string => {
    const colors = ["#E8F4FD", "#B3DEFF", "#64B5F6", "#2196F3"];
    return colors[Math.min(value, colors.length - 1)];
  };

  const getMonthLabels = () => {
    return heatmapData.map((month) => ({
      month: month.month,
      colSpan: month.days.length,
    }));
  };

  const monthLabels = getMonthLabels();

  return (
    <Paper
      elevation={0}
      sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, overflow: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="900"
            sx={{ fontSize: { xs: 32, sm: 38, md: 48 }, mb: 0.5 }}
          >
            {pScore}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            P-Score
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "stretch", md: "flex-end" },
            //flexWrap: "wrap",
            //justifyContent: "flex-end",
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2.5,
              border: "1px solid #E0E0E0",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "auto", sm: 160, md: 180 },
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                backgroundColor: "#F0F4F8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "14px",
                lineHeight: 1,
                alignText: "center",
              }}
            >
              5🔥
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{ lineHeight: 1.2 }}
              >
                Current Streak
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}
              >
                Longest: 6 days
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 1,
              borderRadius: 2.5,
              border: "1px solid #E0E0E0",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "auto", sm: 160, md: 180 },
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                border: "1px solid #0D9488",
                backgroundColor: "#a1ebe5ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.6rem",
                lineHeight: 1,
              }}
            >
              <span style={{ display: "block", marginTop: "-8px" }}>🐢</span>
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{ lineHeight: 1.2 }}
              >
                Lazy Snail
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}
              >
                Late-Comer
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ overflowX: "auto", pb: 2 }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Day labels */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mr: 1,
              mt: 3.5,
            }}
          >
            {["M", "", "", "", "F", "", ""].map((label, idx) => (
              <Typography
                key={idx}
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.7rem",
                  height: 14,
                  lineHeight: "14px",
                  textAlign: "right",
                  width: 16,
                  opacity: label ? 1 : 0.5,
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {/* Month labels and heatmap grid */}
          <Box sx={{ flexGrow: 1, minWidth: "max-content" }}>
            <Box display="flex" gap={1} mb={0.5}>
              {monthLabels.map((label, idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: `0 0 ${Math.max(label.colSpan, 4) * 16}px`,
                    textAlign: "left",
                    minWidth: `${Math.max(label.colSpan, 4) * 16}px`,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    {label.month}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Heatmap grid */}
            <Box display="flex" gap={0.5}>
              {heatmapData.map((month, monthIdx) => (
                <Box key={monthIdx} display="flex" gap={0.5}>
                  {month.days.map((week, weekIdx) => (
                    <Box
                      key={`${monthIdx}-${weekIdx}`}
                      display="flex"
                      flexDirection="column"
                      gap={0.5}
                    >
                      {Array.from({ length: 7 }).map((_, dayIdx) => {
                        const dayValue =
                          week[dayIdx] !== undefined ? week[dayIdx] : 0;
                        return (
                          <Box
                            key={`${monthIdx}-${weekIdx}-${dayIdx}`}
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "2px",
                              backgroundColor: getHeatmapColor(dayValue),
                              border:
                                dayValue === 0 ? "1px solid #f0f0f0" : "none",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.1)",
                                boxShadow: "0 0 0 2px rgba(33, 150, 243, 0.2)",
                                zIndex: 1,
                              },
                            }}
                            title={`${month.month} Week ${
                              weekIdx + 1
                            }, ${getDayName(dayIdx)}: Value ${dayValue}`}
                          />
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Legend */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mt={3}
          justifyContent="center"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem" }}
          >
            Late
          </Typography>
          {[0, 1, 2, 3].map((value) => (
            <Box
              key={value}
              sx={{
                width: 14,
                height: 14,
                borderRadius: "2px",
                backgroundColor: getHeatmapColor(value),
                border: value === 0 ? "1px solid #f0f0f0" : "none",
              }}
            />
          ))}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem" }}
          >
            Early
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default PScoreHeatmap;
